import * as Location from "expo-location";
import { Platform } from "react-native";
import { resolveNearbyPlace } from "./placeResolver";

/* ================================
   TYPES
================================ */

export type CapturedShopPin = {
  lat: number;
  lng: number;
  accuracyMeters: number | null;
  source: "current_location";

  landmarkName?: string | null;
  roadName?: string | null;
  areaName?: string | null;

  confirmedByOwner?: boolean;
};

type ReverseGeocodeResult = {
  roadName: string | null;
  areaName: string | null;
  cityName: string | null;
  regionName: string | null;
  countryName: string | null;
};

type ResolvedPinDetails = {
  landmarkName: string | null;
  roadName: string | null;
  areaName: string | null;
};

type LocationContext = {
  roadName?: string | null;
  areaName?: string | null;
  cityName?: string | null;
  regionName?: string | null;
  countryName?: string | null;
};

/* ================================
   HELPERS
================================ */

function cleanText(value?: unknown): string | null {
  if (typeof value !== "string") return null;

  const cleaned = value
    .replace(/\s+/g, " ")
    .replace(/[.,\s]+$/g, "")
    .trim();

  return cleaned.length > 0 ? cleaned : null;
}

function normalizeForCompare(value?: unknown): string {
  return (
    cleanText(value)
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/gi, " ")
      .trim() ?? ""
  );
}

function sameText(a?: unknown, b?: unknown): boolean {
  const left = normalizeForCompare(a);
  const right = normalizeForCompare(b);

  return !!left && !!right && left === right;
}

function looksLikePlusCode(value?: unknown): boolean {
  const text = cleanText(value)?.toUpperCase();
  if (!text) return false;

  return /^[A-Z0-9]{2,8}\+[A-Z0-9]{2,8}$/.test(text);
}

function pickFirstText(...values: (unknown | null | undefined)[]): string | null {
  for (const value of values) {
    const cleaned = cleanText(value);
    if (cleaned) return cleaned;
  }
  return null;
}

function pickFirstValid(
  values: (unknown | null | undefined)[],
  sanitize: (value: unknown) => string | null
): string | null {
  for (const value of values) {
    const cleaned = sanitize(value);
    if (cleaned) return cleaned;
  }
  return null;
}

function uniqueTexts(values: (string | null | undefined)[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    const cleaned = cleanText(value);
    if (!cleaned) continue;

    const key = normalizeForCompare(cleaned);
    if (!key || seen.has(key)) continue;

    seen.add(key);
    result.push(cleaned);
  }

  return result;
}

function sanitizeRoadCandidate(
  value: unknown,
  context: LocationContext
): string | null {
  const text = cleanText(value);
  if (!text) return null;

  if (looksLikePlusCode(text)) return null;
  if (/^unnamed road$/i.test(text)) return null;

  if (
    sameText(text, context.areaName) ||
    sameText(text, context.cityName) ||
    sameText(text, context.regionName) ||
    sameText(text, context.countryName)
  ) {
    return null;
  }

  return text;
}

function sanitizeAreaCandidate(
  value: unknown,
  context: LocationContext
): string | null {
  const text = cleanText(value);
  if (!text) return null;

  if (looksLikePlusCode(text)) return null;
  if (/^unnamed road$/i.test(text)) return null;

  if (
    sameText(text, context.countryName) ||
    sameText(text, context.roadName)
  ) {
    return null;
  }

  return text;
}

function sanitizeLandmarkCandidate(
  value: unknown,
  context: LocationContext
): string | null {
  const text = cleanText(value);
  if (!text) return null;

  if (looksLikePlusCode(text)) return null;
  if (/^unnamed road$/i.test(text)) return null;

  if (
    sameText(text, context.roadName) ||
    sameText(text, context.areaName) ||
    sameText(text, context.cityName) ||
    sameText(text, context.regionName) ||
    sameText(text, context.countryName)
  ) {
    return null;
  }

  return text;
}

/* ================================
   REVERSE GEOCODE (EXPO FALLBACK)
================================ */

export async function reverseGeocodeShopPin(
  lat: number,
  lng: number
): Promise<ReverseGeocodeResult> {
  try {
    const results = await Location.reverseGeocodeAsync({
      latitude: lat,
      longitude: lng,
    });

    const first = results?.[0];
    if (!first) {
      return {
        roadName: null,
        areaName: null,
        cityName: null,
        regionName: null,
        countryName: null,
      };
    }

    const record = first as Record<string, unknown>;

    const cityName = pickFirstText(
      first.city,
      record.city,
      record.locality
    );

    const regionName = pickFirstText(
      first.region,
      record.region,
      record.subregion
    );

    const countryName = pickFirstText(
      first.country,
      record.country
    );

    const areaName = pickFirstValid(
      [
        record.district,
        record.neighborhood,
        record.neighbourhood,
        record.subLocality,
        record.sublocality,
        record.borough,
        record.village,
        record.locality,
        first.city,
      ],
      (value) =>
        sanitizeAreaCandidate(value, {
          cityName,
          regionName,
          countryName,
        })
    );

    const roadName = pickFirstValid(
      [
        first.street,
        record.street,
        record.streetName,
        record.route,
      ],
      (value) =>
        sanitizeRoadCandidate(value, {
          areaName,
          cityName,
          regionName,
          countryName,
        })
    );

    return {
      roadName,
      areaName,
      cityName,
      regionName,
      countryName,
    };
  } catch {
    console.warn("Shop pin reverse geocoding failed.");
    return {
      roadName: null,
      areaName: null,
      cityName: null,
      regionName: null,
      countryName: null,
    };
  }
}

/* ================================
   MERGE LOGIC
================================ */

export async function resolveShopPinDetails(
  lat: number,
  lng: number
): Promise<ResolvedPinDetails> {
  try {
    const [reverse, place] = await Promise.all([
      reverseGeocodeShopPin(lat, lng),
      resolveNearbyPlace(lat, lng),
    ]);

    const areaName = pickFirstValid(
      [place?.areaName, reverse.areaName],
      (value) =>
        sanitizeAreaCandidate(value, {
          cityName: reverse.cityName,
          regionName: reverse.regionName,
          countryName: reverse.countryName,
        })
    );

    const roadName = pickFirstValid(
      [place?.roadName, reverse.roadName],
      (value) =>
        sanitizeRoadCandidate(value, {
          areaName,
          cityName: reverse.cityName,
          regionName: reverse.regionName,
          countryName: reverse.countryName,
        })
    );

    const landmarkName = pickFirstValid(
      [place?.landmarkName],
      (value) =>
        sanitizeLandmarkCandidate(value, {
          roadName,
          areaName,
          cityName: reverse.cityName,
          regionName: reverse.regionName,
          countryName: reverse.countryName,
        })
    );

    return {
      landmarkName,
      roadName,
      areaName,
    };
  } catch {
    console.warn("Shop pin detail resolution failed.");
    return {
      landmarkName: null,
      roadName: null,
      areaName: null,
    };
  }
}

/* ================================
   CAPTURE LOCATION
================================ */

export async function captureBestShopPin(): Promise<CapturedShopPin> {
  if (Platform.OS === "web") {
    const hasLocationService = await Location.hasServicesEnabledAsync();

    if (!hasLocationService) {
      throw new Error(
        "Location is not available in this browser. Please enable location services and try again."
      );
    }
  } else {
    const permission = await Location.requestForegroundPermissionsAsync();

    if (!permission.granted && permission.status !== "granted") {
      throw new Error("Location permission was denied.");
    }
  }

  let current: Location.LocationObject;

  try {
    current = await Location.getCurrentPositionAsync({
      accuracy: Location.Accuracy.High,
    });
  } catch (error) {
    throw new Error(
      error instanceof Error && error.message
        ? error.message
        : "Unable to capture your current location."
    );
  }

  const lat = current.coords.latitude;
  const lng = current.coords.longitude;

  const accuracyMeters =
    typeof current.coords.accuracy === "number"
      ? Math.round(current.coords.accuracy)
      : null;

  const resolved = await resolveShopPinDetails(lat, lng);

  return {
    lat,
    lng,
    accuracyMeters,
    source: "current_location",
    landmarkName: resolved.landmarkName,
    roadName: resolved.roadName,
    areaName: resolved.areaName,
    confirmedByOwner: false,
  };
}

/* ================================
   MAP URL HELPERS
================================ */

export function getGoogleMapsUrl(
  lat: number,
  lng: number,
  label?: string
): string {
  const safeLabel = cleanText(label);

  if (safeLabel) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${safeLabel} ${lat},${lng}`
    )}`;
  }

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${lat},${lng}`
  )}`;
}

export function getGoogleEmbedMapUrl(lat: number, lng: number): string {
  return `https://maps.google.com/maps?q=${lat},${lng}&z=16&output=embed`;
}

/* ================================
   UI HELPERS
================================ */

export function buildPinTitle(pin: CapturedShopPin | null): string {
  if (!pin) return "No location captured";

  return (
    cleanText(pin.landmarkName) ||
    cleanText(pin.roadName) ||
    cleanText(pin.areaName) ||
    "Pinned business location"
  );
}

export function buildPinSubtitle(pin: CapturedShopPin | null): string {
  if (!pin) return "";

  const title = buildPinTitle(pin);

  return uniqueTexts([pin.roadName, pin.areaName])
    .filter((value) => !sameText(value, title))
    .join(", ");
}

export function buildFullAddress(pin: CapturedShopPin | null): string {
  if (!pin) return "";

  return uniqueTexts([
    pin.landmarkName,
    pin.roadName,
    pin.areaName,
  ]).join(", ");
}
